<template>
  <section v-if="visible" class="offer-terms">
    <h2 class="offer-terms__title">Offer terms</h2>
    <p class="offer-terms__lead">{{ lead }}</p>

    <dl class="offer-terms__list">
      <div v-if="guidance" class="offer-terms__row">
        <dt>{{ isRateBased ? "Rate range" : "Price range" }}</dt>
        <dd>
          {{ guidance }}
          <span class="offer-terms__note">
            The final {{ isRateBased ? "rate" : "price" }} is set after the offer closes.
          </span>
        </dd>
      </div>

      <div v-if="token.reserve_price" class="offer-terms__row">
        <dt>Reserve</dt>
        <dd>
          {{ formatNumber(token.reserve_price) }}
          <span class="offer-terms__note">
            Bids beyond the reserve are not allotted, however much of the offer is left.
          </span>
        </dd>
      </div>

      <div v-if="token.price_guidance && token.price_guidance.step" class="offer-terms__row">
        <dt>Bid increment</dt>
        <dd>
          {{ formatNumber(token.price_guidance.step) }}
          <span class="offer-terms__note">Bids must land on a multiple of this.</span>
        </dd>
      </div>

      <div class="offer-terms__row">
        <dt>Offer period</dt>
        <dd>
          <template v-if="token.offer_opens_at">
            {{ formatOfferMoment(token.offer_opens_at, token.market_timezone) }} to
          </template>
          {{ formatOfferMoment(token.offer_closes_at, token.market_timezone) }}
        </dd>
      </div>

      <!-- Only an auction has a convention to state, and it decides what a winning bid costs.
           Ranking tells an investor whether they are filled; this tells them at what. -->
      <div v-if="pricingDisclosure" class="offer-terms__row">
        <dt>What you pay</dt>
        <dd>
          {{ pricingDisclosure }}
          <span v-if="strikeSettlesAtClearing" class="offer-terms__note">
            A bid at the final {{ isRateBased ? "rate" : "price" }} settles at the clearing
            level, because it named none.
          </span>
        </dd>
      </div>

      <div v-if="allotmentDisclosure" class="offer-terms__row">
        <dt>If it is oversubscribed</dt>
        <dd>{{ allotmentDisclosure }}</dd>
      </div>

      <div v-if="underSubscriptionDisclosure" class="offer-terms__row">
        <dt>If it is undersubscribed</dt>
        <dd>{{ underSubscriptionDisclosure }}</dd>
      </div>

      <div class="offer-terms__row">
        <dt>Your funds</dt>
        <dd>
          Committing places the funds in escrow. It is released when the
          allotment is approved, and anything you are not allotted comes back to your wallet then.
        </dd>
      </div>
    </dl>
  </section>
</template>

<script>
import {
  ALLOTMENT_METHOD_DISCLOSURE,
  UNDERSUBSCRIPTION_DISCLOSURE,
  AuctionPricing,
  OfferingTypes,
  auctionPricingDisclosure,
  formatOfferMoment,
  hasOfferPeriod,
} from "@/utils/offering";
import { convertToLocale } from "@/utils/helpers.js";

// Matches the API's isRateBased, which is debt and fixed interest only. A fund clears at a
// price, so describing its terms as a rate would misstate what a bidder is naming.
const RATE_BASED = ["Debt", "Fixed Interest"];

export default {
  name: "OfferTerms",

  props: {
    token: { type: Object, required: true },
  },

  computed: {
    // Only offerings with a period have terms of this kind. A continuously filling deal is
    // bought at a stated price and settles on the spot — there is no allotment to explain.
    visible() {
      return hasOfferPeriod(this.token);
    },

    isRateBased() {
      return RATE_BASED.includes(this.token.investment_type);
    },

    // The name on the deal, not the market's word for the role.
    issuerName() {
      return this.token?.creator?.name || "The issuer";
    },

    lead() {
      switch (this.token.offering_type) {
        case OfferingTypes.BOOK_BUILDING:
          return `You commit an amount and the ${
            this.isRateBased ? "rate" : "price"
          } you are willing to accept. Once the offer closes, ${this.issuerName} sets a final ${
            this.isRateBased ? "rate" : "price"
          } from the demand received, and the offer is shared out among the bids that qualify.`;
        case OfferingTypes.AUCTION:
          return "You bid competitively. Bids are ranked from best to worst and filled in that order until the offer is exhausted.";
        default:
          return "You commit at the stated price. Nothing is settled until the offer closes.";
      }
    },

    guidance() {
      const g = this.token.price_guidance;
      if (!g || g.floor === undefined || g.cap === undefined) return "";
      const unit = this.isRateBased ? "%" : "";
      return `${this.formatNumber(g.floor)}${unit} to ${this.formatNumber(g.cap)}${unit}`;
    },

    pricingDisclosure() {
      return auctionPricingDisclosure(this.token, this.isRateBased);
    },

    // Worth calling out only where it changes the answer: under pay-your-bid a strike bid is
    // settled differently from every named bid around it.
    strikeSettlesAtClearing() {
      return (
        this.token.offering_type === OfferingTypes.AUCTION
        && this.token.allow_strike_bids
        && this.token.auction_pricing === AuctionPricing.MULTIPLE
      );
    },

    allotmentDisclosure() {
      return ALLOTMENT_METHOD_DISCLOSURE[this.token.allotment_method] || "";
    },

    underSubscriptionDisclosure() {
      return UNDERSUBSCRIPTION_DISCLOSURE[this.token.undersubscription_policy] || "";
    },
  },

  methods: {
    formatOfferMoment,
    formatNumber(value) {
      return convertToLocale(value);
    },
  },
};
</script>

<style lang="scss" scoped>
$text-primary: #0f172a;
$text-secondary: #475569;
$text-muted: #64748b;
$border-color: #e2e8f0;
$bg-subtle: #ffffff;

.offer-terms {
  background: $bg-subtle;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 20px;

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $text-primary;
    margin: 0 0 8px;
  }

  &__lead {
    font-size: 13px;
    line-height: 1.6;
    color: $text-secondary;
    margin: 0 0 16px;
    max-width: 62ch;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0;
  }

  &__row {
    display: grid;
    grid-template-columns: 180px minmax(0, 1fr);
    gap: 4px 16px;
    padding-top: 12px;
    border-top: 1px solid $border-color;

    dt {
      font-size: 13px;
      color: $text-muted;
    }

    dd {
      font-size: 13px;
      line-height: 1.6;
      color: $text-primary;
      margin: 0;
      font-weight: 500;
    }
  }

  &__note {
    display: block;
    font-weight: 400;
    color: $text-secondary;
  }

  @media (max-width: 640px) {
    &__row {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}
</style>
